import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { site } from '@shared/site.config';
import BrandMark from '@/components/BrandMark';

/**
 * Sheet numbers, as on a drawing set. Navigation is an index of sheets, which
 * is why each item carries its designation rather than an icon.
 */
const NAV = [
  { to: '/buying-selling', label: 'Buying & Selling', sheet: 'A-01' },
  { to: '/leasing', label: 'Leasing', sheet: 'A-02' },
  { to: '/business-listings', label: 'Business Listings', sheet: 'A-03' },
  { to: '/commercial-property-value-calculator', label: 'Owner Tools', sheet: 'T-01' },
  { to: '/kwc-guelph-commercial-owner-brief', label: 'Owner Brief', sheet: 'R-01' },
  { to: '/about', label: 'About', sheet: 'A-04' },
  { to: '/contact', label: 'Contact', sheet: 'A-05' },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 bg-sheet/95 backdrop-blur-md transition-colors ${
        scrolled ? 'border-b border-medline' : 'border-b border-hairline'
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex h-[66px] items-center justify-between gap-6">
          <Link to="/" className="group flex items-center gap-3 shrink-0" aria-label={`${site.brand.name} — home`}>
            <BrandMark className="h-9 w-9" />
            <span className="font-display text-[19px] font-semibold leading-none tracking-tight">
              {site.brand.name}
            </span>
          </Link>

          <nav className="hidden xl:flex items-stretch">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `group relative flex flex-col justify-center px-3 py-2 font-mono text-[11px] uppercase
                   tracking-wider2 whitespace-nowrap transition-colors ${
                     isActive ? 'text-graphite' : 'text-annot hover:text-graphite'
                   }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span className={`text-[9px] tracking-wider3 ${isActive ? 'text-redline' : 'text-medline'}`}>
                      {item.sheet}
                    </span>
                    <span className="mt-1">{item.label}</span>
                    <span
                      className={`absolute bottom-0 left-3 right-3 h-[2px] origin-left bg-redline
                                  transition-transform duration-200 ${isActive ? 'scale-x-100' : 'scale-x-0'}`}
                    />
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="hidden xl:flex items-center gap-4">
            <a
              href={`tel:${site.agent.phone.replace(/\D/g, '')}`}
              className="font-mono text-[11px] tracking-wider2 text-annot hover:text-graphite whitespace-nowrap"
            >
              {site.agent.phone}
            </a>
            <Link to="/contact" className="btn-primary whitespace-nowrap">
              Get in touch
            </Link>
          </div>

          <button
            onClick={() => setOpen((v) => !v)}
            className="xl:hidden -mr-2 p-2 text-graphite"
            aria-expanded={open}
            aria-label="Toggle navigation"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              {open ? <path d="M18 6L6 18M6 6l12 12" /> : <path d="M3 7h18M3 12h18M3 17h18" />}
            </svg>
          </button>
        </div>
      </div>

      {open && (
        <div className="animate-fade-in xl:hidden border-t border-hairline bg-sheet">
          <nav className="mx-auto max-w-7xl px-5 py-2">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className="flex items-baseline gap-4 border-b border-hairline py-3 last:border-b-0"
              >
                <span className="sheet-no w-9 shrink-0">{item.sheet}</span>
                <span className="font-display text-[17px]">{item.label}</span>
              </NavLink>
            ))}
            <a
              href={`tel:${site.agent.phone.replace(/\D/g, '')}`}
              className="mt-3 flex items-baseline gap-4 py-3 font-mono text-[13px] tracking-wider2 text-redline"
            >
              <span className="w-9 shrink-0 text-[10px] text-medline">TEL</span>
              {site.agent.phone}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
